(() => {
  'use strict';
  const blocked = () => document.querySelector('[data-vf-shipping-blocked]');
  const root = window.Shopify?.routes?.root || '/';
  let running = false;
  let timer;

  const ready = (cart) => Array.isArray(cart?.items) && cart.items.every((item) => {
    if (!item.properties?.['Requested cut length']) return true;
    const expected = Number(item.properties['_Estimated material weight grams']);
    const native = Number(item.grams);
    return Number.isFinite(expected) && Number.isFinite(native)
      && expected > 0 && native > 0 && Math.abs(expected - native) <= 5
      && Number(item.original_price) > 0;
  });

  const getJSON = async (url) => {
    const response = await fetch(url, {
      cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(6000),
    });
    if (!response.ok) throw new Error('Cart unavailable');
    return response.json();
  };

  // Re-render the prices AND checkout controls from Shopify's current cart.
  // Keep the drawer shell, its overlay listener, and any unsaved cart note.
  const refreshSections = async (cart) => {
    const targets = [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'main-cart-items', section: document.getElementById('main-cart-items')?.dataset.id, selector: '.js-contents' },
      { id: 'main-cart-footer', section: document.getElementById('main-cart-footer')?.dataset.id, selector: '.cart__blocks' },
    ].filter((target) => target.section && document.getElementById(target.id));
    const url = new URL(root + 'cart', window.location.origin);
    url.searchParams.set('sections', targets.map((target) => target.section).join(','));
    const sections = await getJSON(url);
    const replacements = targets.map((target) => {
      if (!sections[target.section]) throw new Error('Cart display unavailable');
      const html = new DOMParser().parseFromString(sections[target.section], 'text/html');
      if (html.querySelector('[data-vf-shipping-blocked]')) throw new Error('Cart still updating');
      const source = html.querySelector(target.selector);
      const parent = document.getElementById(target.id);
      const destination = parent?.querySelector(target.selector) || parent;
      if (!source || !destination) throw new Error('Cart display unavailable');
      return { source, destination };
    });
    // A quantity change or another add owns the UI until its request completes.
    if (document.querySelector('.cart__items--disabled, [data-vf-cut-selector] .submitting')) return false;
    const focus = document.activeElement;
    const focusId = focus?.id;
    const losesFocus = replacements.some(({ destination }) => destination.contains(focus));
    const notes = [...document.querySelectorAll('textarea[name="note"]')].map((note) => ({ id: note.id, value: note.value }));
    replacements.forEach(({ source, destination }) => { destination.innerHTML = source.innerHTML; });
    notes.forEach((note) => { const element = document.getElementById(note.id); if (element) element.value = note.value; });
    document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-footer')
      .forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));
    const drawer = document.querySelector('cart-drawer.active');
    if (drawer && typeof trapFocus === 'function') {
      const container = drawer.querySelector('.drawer__inner');
      const nextFocus = (focusId && document.getElementById(focusId)) || container?.querySelector('.drawer__close') || container;
      trapFocus(container, losesFocus ? nextFocus : focus);
    } else if (losesFocus && focusId) {
      document.getElementById(focusId)?.focus({ preventScroll: true });
    }
    document.dispatchEvent(new CustomEvent('vf:cart-ready', { detail: cart }));
    return true;
  };

  const recover = async () => {
    if (running || !blocked()) return;
    running = true;
    const deadline = Date.now() + 60000;
    try {
      do {
        if (!blocked()) return;
        try {
          const cart = await getJSON(root + 'cart.js');
          if (ready(cart) && await refreshSections(cart)) return;
        } catch { /* Keep checkout protected; retry transient cart/section lag. */ }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } while (Date.now() < deadline);
    } finally {
      running = false;
    }
  };
  const schedule = () => {
    if (running || timer || !blocked()) return;
    timer = setTimeout(() => { timer = null; void recover(); }, 150);
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[name="checkout"]') && blocked()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      blocked().scrollIntoView({ block: 'center' });
      schedule();
    }
    if (event.target.closest('#cart-icon-bubble')) schedule();
  }, true);
  document.addEventListener('submit', (event) => {
    if (!event.target.matches('#cart, #CartDrawer-Form') || !blocked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    blocked().scrollIntoView({ block: 'center' });
    schedule();
  }, true);
  const start = () => {
    // DPO can insert a pending cut after initial page load, including in a closed drawer.
    const observer = new MutationObserver(schedule);
    ['CartDrawer', 'main-cart-footer'].forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element, { childList: true, subtree: true });
    });
    schedule();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
})();
